import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSharedProperties } from "../../redux/slices/analysisSlice";
import { cleanAddress } from "../../utils/addressUtils";
import {
    Loader2,
    MapPin,
    Calendar,
    Mail,
    X,
    Users,
    Clock,
    Inbox,
    Send,
    Eye,
    Map
} from "lucide-react";
import PropertyDetailModal from "../Modal/PropertyDetailModal";
import MapModal from "../Modal/MapModal";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Custom Tooltip Component
const Tooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div
            className="relative flex items-center justify-center"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {children}
            {isVisible && (
                <div className="absolute bottom-full mb-2 z-50 whitespace-nowrap">
                    <div className="bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-xl relative animate-in fade-in zoom-in-95 duration-200">
                        {content}
                        <div className="absolute w-2 h-2 bg-slate-900 rotate-45 left-1/2 -translate-x-1/2 -bottom-1"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SharedProperties = () => {
    const dispatch = useDispatch();
    const { sharedProperties, isLoadingHistory } = useSelector(
        (state) => state.analysis
    );
    const { hasPro } = useFeatureAccess();
    const navigate = useNavigate();

    // Modal Interaction State
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    const [activeTab, setActiveTab] = useState("received"); // 'received' | 'sent'

    // State for the "Show All Users" modal
    const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
    const [selectedPropertyUsers, setSelectedPropertyUsers] = useState([]);
    const [selectedPropertyAddress, setSelectedPropertyAddress] = useState("");

    useEffect(() => {
        dispatch(fetchSharedProperties());
    }, [dispatch]);

    // Helper to group shares by property_id
    const groupShares = (items) => {
        if (!items) return [];
        const groups = {};

        items.forEach(item => {
            if (!groups[item.property_id]) {
                groups[item.property_id] = {
                    ...item,
                    users: []
                };
            }
            // Add the other party to the users list
            groups[item.property_id].users.push({
                name: item.other_party_name,
                email: item.other_party_email,
                avatar: item.other_party_name ? item.other_party_name.charAt(0).toUpperCase() : 'U'
            });
        });

        // Convert back to array
        return Object.values(groups);
    };

    const handleShowAllUsers = (users, address) => {
        setSelectedPropertyUsers(users);
        setSelectedPropertyAddress(address);
        setIsUsersModalOpen(true);
    };

    const handleViewDetails = (item) => {
        setSelectedAnalysis(item);
        setIsDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedAnalysis(null);
    };

    const handleViewMap = (item, type) => {
        if (hasPro || type === 'received') {
            setSelectedAnalysis(item);
            setIsMapModalOpen(true);
        } else {
            toast.info(
                <div className="flex flex-col gap-2">
                    <span className="font-bold">Pro Feature</span>
                    <span>Upgrade to Professional plan to view interactive maps.</span>
                    <button
                        onClick={() => navigate('/#subscription-plans')}
                        className="mt-2 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50"
                    >
                        View Plans
                    </button>
                </div>,
                { autoClose: 5000 }
            );
        }
    };

    const handleCloseMapModal = () => {
        setIsMapModalOpen(false);
        setSelectedAnalysis(null);
    };

    const renderAvatars = (users, address) => {
        const displayLimit = 3;
        const extraCount = users.length - displayLimit;
        const visibleUsers = users.slice(0, displayLimit);

        return (
            <div className="flex items-center">
                <div className="flex -space-x-3 transition-all duration-300">
                    {visibleUsers.map((u, i) => (
                        <div key={i} style={{ zIndex: 10 - i }} className="relative group/avatar">
                            <Tooltip content={`${u.name || 'Unknown'} (${u.email})`}>
                                <div
                                    className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-md relative hover:z-20 transform hover:-translate-y-1 transition-all cursor-pointer ring-2 ring-transparent hover:ring-blue-200"
                                >
                                    {u.avatar}
                                </div>
                            </Tooltip>
                        </div>
                    ))}

                    {extraCount > 0 && (
                        <Tooltip content={`Click to view ${extraCount} more recipients`}>
                            <div
                                onClick={() => handleShowAllUsers(users, address)}
                                className="w-10 h-10 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold shadow-md cursor-pointer hover:bg-slate-200 hover:scale-105 transition-all z-0"
                            >
                                +{extraCount}
                            </div>
                        </Tooltip>
                    )}
                </div>

                {users.length === 1 && (
                    <div className="ml-3 flex flex-col justify-center animate-in fade-in slide-in-from-left-2">
                        <span className="text-sm font-bold text-slate-800">{users[0].name || 'Unknown'}</span>
                        <span className="text-xs text-slate-500 font-medium">{users[0].email}</span>
                    </div>
                )}
            </div>
        );
    };

    const renderList = (rawItems, type) => {
        const items = groupShares(rawItems);

        if (!items || items.length === 0) {
            return (
                <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-transparent opacity-50" />
                    <div className="relative z-10 flex flex-col items-center">
                        <div className="bg-slate-50 p-4 rounded-full mb-4 shadow-inner group-hover:scale-110 transition-transform duration-300">
                            {type === 'sent' ? (
                                <Send className="w-8 h-8 text-slate-400" />
                            ) : (
                                <Inbox className="w-8 h-8 text-slate-400" />
                            )}
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                            No {type === 'sent' ? 'sent' : 'received'} properties
                        </h3>
                        <p className="text-slate-500 max-w-sm mx-auto">
                            {type === 'sent'
                                ? "Properties you share with others will appear here."
                                : "When someone shares a property with you, it will show up here."}
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden ring-1 ring-slate-200/50">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-blue-600 text-white border-b border-blue-700">
                            <tr>
                                <th className="px-8 py-5 text-left text-xs font-bold uppercase tracking-widest text-blue-100">
                                    <div className="flex items-center gap-2 group">
                                        <MapPin className="w-4 h-4 text-blue-200 group-hover:text-white transition-colors" />
                                        <span>Property Address</span>
                                    </div>
                                </th>
                                <th className="px-8 py-5 text-left text-xs font-bold uppercase tracking-widest text-blue-100">
                                    <div className="flex items-center gap-2 group">
                                        <Users className="w-4 h-4 text-blue-200 group-hover:text-white transition-colors" />
                                        <span>{type === 'sent' ? 'Shared With' : 'Shared By'}</span>
                                    </div>
                                </th>
                                <th className="px-8 py-5 text-left text-xs font-bold uppercase tracking-widest text-blue-100">
                                    <div className="flex items-center gap-2 group">
                                        <Calendar className="w-4 h-4 text-blue-200 group-hover:text-white transition-colors" />
                                        <span>Shared On</span>
                                    </div>
                                </th>
                                <th className="px-8 py-5 text-center text-xs font-bold uppercase tracking-widest text-blue-100">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {items.map((item) => (
                                <tr key={item.id} className="group hover:bg-blue-50/30 transition-all duration-300 border-l-4 border-transparent hover:border-blue-500">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center space-x-4">
                                            <div className="bg-slate-50 p-2.5 rounded-2xl border border-slate-100 group-hover:border-blue-200 group-hover:bg-white group-hover:shadow-md transition-all duration-300">
                                                <div className="bg-white p-2 rounded-xl shadow-sm text-blue-600 group-hover:bg-blue-50 transition-colors">
                                                    <MapPin className="w-5 h-5" />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-900 text-sm group-hover:text-blue-700 transition-colors">{cleanAddress(item.address)}</div>
                                                <div className="text-xs text-slate-500 font-medium mt-1 bg-slate-100 px-2 py-0.5 rounded-md w-fit group-hover:bg-blue-100/50 group-hover:text-blue-600 transition-colors">
                                                    {item.apn || 'No APN'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        {renderAvatars(item.users, item.address)}
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center space-x-2 text-sm text-slate-600 font-medium group-hover:text-slate-900 transition-colors">
                                            <div className="p-1.5 bg-slate-100 rounded-lg group-hover:bg-blue-100/50 group-hover:text-blue-600 transition-colors">
                                                <Clock className="w-4 h-4" />
                                            </div>
                                            <span className="font-mono text-xs">{new Date(item.shared_at).toLocaleDateString(undefined, {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center justify-center space-x-3">
                                            <button
                                                onClick={() => handleViewMap(item, type)}
                                                className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 ${ (hasPro || type === 'received')
                                                    ? "bg-emerald-100 border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:shadow-lg hover:shadow-emerald-500/30"
                                                    : "bg-slate-100 border border-slate-200 text-slate-400 hover:bg-slate-200 hover:text-slate-500 cursor-pointer"
                                                    }`}
                                                title={(hasPro || type === 'received') ? "View on Map" : "Upgrade to view Map"}
                                            >
                                                <Map className="w-5 h-5" />
                                            </button>

                                            <button
                                                onClick={() => handleViewDetails(item)}
                                                className="w-10 h-10 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-700 shadow-sm transition-all duration-300 hover:bg-blue-600 hover:text-white hover:shadow-lg hover:shadow-blue-600/30 hover:scale-105 hover:-translate-y-0.5"
                                                title="View Details"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (isLoadingHistory && !sharedProperties.sent.length && !sharedProperties.received.length) {
        return (
            <div className="flex justify-center items-center py-32">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="animate-spin text-blue-600 w-12 h-12" />
                    <p className="text-slate-500 font-medium animate-pulse">Loading shares...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pb-10">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 bg-white p-6 md:p-8 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/40 relative overflow-hidden">
                {/* Decorative Background Blob */}
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl" />

                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2.5 rounded-xl shadow-lg shadow-blue-500/20 text-white transform">
                            <Users className="w-6 h-6" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Shared Properties</h1>
                    </div>
                    <p className="text-slate-500 max-w-lg leading-relaxed text-sm md:text-base pl-1">
                        Manage your collaborative analysis network. Track properties shared with you and by you.
                    </p>
                </div>

                {/* Premium 3D Fluid Switch - Refined Design */}
                <div className="relative z-10 self-stretch md:self-auto min-w-[340px]">
                    <div className="bg-slate-100 p-1.5 rounded-xl border border-slate-200/60 shadow-inner flex relative overflow-hidden group/switch hover:border-slate-300 transition-colors duration-300">

                        {/* The Sliding Puck */}
                        <div
                            className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-xl shadow-md transition-all duration-500 cubic-bezier(0.34, 1.56, 0.64, 1) z-10 border ${activeTab === 'received'
                                ? 'left-1.5 bg-blue-600 border-blue-400/30'
                                : 'left-[calc(50%+3px)] bg-indigo-600 border-indigo-400/30'
                                }`}
                        >
                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-50 rounded-xl" />

                            {/* Active Indicator Bar (White) */}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-white/40 rounded-t-full" />
                        </div>

                        {/* Received Button */}
                        <button
                            onClick={() => setActiveTab('received')}
                            className={`flex-1 relative z-20 py-3 text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2.5 rounded-xl select-none ${activeTab === 'received'
                                ? 'text-white'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <div className={`p-1.5 rounded-lg transition-all duration-500 ${activeTab === 'received' ? 'bg-white/20 text-white' : 'bg-transparent text-slate-400'}`}>
                                <Inbox className={`w-4 h-4 transition-transform duration-500 ${activeTab === 'received' ? 'scale-110' : 'scale-100'}`} />
                            </div>
                            <span className="tracking-wide">Received</span>

                            {sharedProperties.received?.length > 0 && (
                                <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[10px] font-extrabold shadow-sm transition-all duration-500 border ${activeTab === 'received'
                                    ? 'bg-white text-blue-600 border-white scale-100'
                                    : 'bg-slate-200 text-slate-500 border-transparent scale-90'
                                    }`}>
                                    {sharedProperties.received?.length}
                                </span>
                            )}
                        </button>

                        {/* Sent Button */}
                        <button
                            onClick={() => setActiveTab('sent')}
                            className={`flex-1 relative z-20 py-3 text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2.5 rounded-xl select-none ${activeTab === 'sent'
                                ? 'text-white'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <div className={`p-1.5 rounded-lg transition-all duration-500 ${activeTab === 'sent' ? 'bg-white/20 text-white' : 'bg-transparent text-slate-400'}`}>
                                <Send className={`w-4 h-4 transition-transform duration-500 ${activeTab === 'sent' ? 'scale-110' : 'scale-100'}`} />
                            </div>
                            <span className="tracking-wide">Sent</span>
                            {sharedProperties.sent?.length > 0 && (
                                <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[10px] font-extrabold shadow-sm transition-all duration-500 border ${activeTab === 'sent'
                                    ? 'bg-white text-indigo-600 border-white scale-100'
                                    : 'bg-slate-200 text-slate-500 border-transparent scale-90'
                                    }`}>
                                    {sharedProperties.sent?.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div key={activeTab} className="animate-in fade-in mt-5 slide-in-from-bottom-8 duration-500 fill-mode-forwards">
                {activeTab === 'received'
                    ? renderList(sharedProperties.received, 'received')
                    : renderList(sharedProperties.sent, 'sent')
                }
            </div>

            {/* Users List Modal */}
            {isUsersModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
                    <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl border border-slate-100 overflow-hidden transform transition-all scale-100">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-center text-white">
                            <div>
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Shared With
                                </h3>
                                <p className="text-xs text-blue-100 font-medium truncate max-w-[250px] mt-1 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                                    {cleanAddress(selectedPropertyAddress)}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsUsersModalOpen(false)}
                                className="p-2 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 max-h-[400px] overflow-y-auto space-y-2">
                            {selectedPropertyUsers.map((user, idx) => (
                                <div key={idx} className="flex items-center space-x-4 p-4 hover:bg-slate-50 rounded-2xl transition-all group border border-transparent hover:border-slate-100 hover:shadow-sm">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold shadow-md group-hover:scale-110 transition-transform ring-4 ring-blue-50">
                                        {user.avatar}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{user.name || 'Unknown'}</p>
                                        <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                                            <Mail className="w-3 h-3" />
                                            {user.email}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {/* Property Detail Modal */}
            <PropertyDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                analysis={selectedAnalysis}
            />

            {/* Map Modal */}
            <MapModal
                isOpen={isMapModalOpen}
                onClose={handleCloseMapModal}
                address={cleanAddress(selectedAnalysis?.address)}
                apn={selectedAnalysis?.apn}
                county={selectedAnalysis?.county}
                sr_wkid={selectedAnalysis?.sr_wkid}
                parcelAreaAcres={selectedAnalysis?.parcel_area_acres}
                mda={selectedAnalysis?.mda_ft2}
                mfa={selectedAnalysis?.mfa_ft2}
                grossBuildingArea={selectedAnalysis?.gross_building_area_ft2}
                livingArea={selectedAnalysis?.living_area_ft2}
                avgSlope={selectedAnalysis?.avg_slope_percent_LAH}
                analysisId={selectedAnalysis?.id}
            />
        </div>
    );
};
export default SharedProperties;
